import "@/lib/immer-config"; // Ensure MapSet is enabled
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useMemo } from "react";
import { ViewerPreferencesSchema } from "@/schemas/storage";
import {
  Component,
  Project,
  Page,
  Viewport,
  CanvasSettings,
  ProjectVersion,
  PageSnapshot,
  PageTemplate,
  ProjectRole,
} from "@/types";
import { isContainerType } from "@/utils/componentTypes";
import { migrateLegacyTemplateComponents } from "@/utils/componentTreeMigrations";
import { migrateProjectThemeColors } from "@/utils/project-theme-color-migration";
import { apiClient, getApiErrorMessage } from "@/services/api/client";
import { responsivePositionTranslator } from "@/services/responsive/ResponsivePositionTranslator";
import { useNavigationStore } from "@/stores/navigation";
import { v4 as uuidv4 } from "uuid";

// Default canvas settings for viewer (read-only optimized)
const defaultViewerCanvasSettings: CanvasSettings = {
  background: {
    type: "color",
    color: "#f9fafb",
  },
  grid: {
    enabled: false,
    size: 20,
    color: "#e5e7eb",
    opacity: 0.3, // Lighter grid for viewer
    style: "solid",
    majorLines: {
      enabled: true,
      interval: 5,
      color: "#d1d5db",
      opacity: 0.5, // Lighter major lines for viewer
    },
    snapToGrid: false, // No snapping in viewer mode
    snapThreshold: 10,
  },
  layout: {
    padding: {
      top: 40,
      right: 40,
      bottom: 40,
      left: 40,
    },
    maxWidth: null,
    maxHeight: null,
    centerContent: false,
    overflow: "visible",
  },
  theme: {
    name: "Default",
    colorScheme: "light",
    accentColor: "#3b82f6",
    borderRadius: 8,
    shadows: true,
    animations: true,
  },
  responsive: {
    breakpoints: {
      desktop: 1200,
      tablet: 768,
      mobile: 480,
    },
    scaling: {
      desktop: 1,
      tablet: 0.8,
      mobile: 0.6,
    },
    hideElementsOnMobile: false,
    touchOptimization: true,
  },
};

// Helper function to build a complete component map including nested children
const buildCompleteComponentMap = (components: Component[]): Map<string, Component> => {
  const map = new Map<string, Component>();

  const addComponentAndChildren = (component: Component) => {
    map.set(component.id, component);

    // If component has _childComponents, add them recursively
    if (component._childComponents && Array.isArray(component._childComponents)) {
      component._childComponents.forEach((child) => {
        addComponentAndChildren(child);
      });
    }
  };

  components.forEach(component => addComponentAndChildren(component));
  return map;
};

const migrateViewerComponents = (components: Component[]): Component[] =>
  migrateProjectThemeColors(migrateLegacyTemplateComponents(components));

const getVersionCreatedAtMs = (version: { createdAt?: Date | string }): number => {
  if (!version.createdAt) {
    return 0;
  }

  return new Date(version.createdAt).getTime();
};

interface WrappedPageRecord {
  page: Partial<Page>;
  components?: Partial<Component>[];
}

const isWrappedPageRecord = (obj: unknown): obj is WrappedPageRecord => {
  return obj !== null &&
    typeof obj === "object" &&
    "page" in obj &&
    typeof (obj as WrappedPageRecord).page === "object";
};

const isSnapshotPageRecord = (obj: unknown): obj is PageSnapshot => {
  return obj !== null &&
    typeof obj === "object" &&
    "pageId" in obj &&
    typeof (obj as PageSnapshot).pageId === "string";
};

// Helper function to enhance components with _childComponents for FlexContainers
const enhanceComponentsWithChildren = (components: Component[]): Component[] => {
  const componentMap = new Map(components.map(c => [c.id, c]));
  const childComponentIds = new Set<string>();

  // First pass: collect all child component IDs
  // Method 1: From children array
  components.forEach(component => {
    if (isContainerType(component.type) && component.children?.length > 0) {
      component.children.forEach(childId => childComponentIds.add(childId));
    }
  });

  // Method 2: From parentId (fallback for flat rendering model)
  // Build a map of parentId -> children
  const parentIdChildrenMap = new Map<string, Component[]>();
  components.forEach(component => {
    if (component.parentId && component.parentId !== 'root') {
      const siblings = parentIdChildrenMap.get(component.parentId) || [];
      siblings.push(component);
      parentIdChildrenMap.set(component.parentId, siblings);
      childComponentIds.add(component.id);
    }
  });

  // Recursive function to enhance a single component
  const enhanceComponent = (component: Component): Component => {
    if (!isContainerType(component.type)) {
      return component;
    }

    // Find children from children array OR from parentId lookup
    let childComponents: Component[] = [];

    if (component.children?.length > 0) {
      // Use children array if available
      childComponents = component.children
        .map(childId => componentMap.get(childId))
        .filter((child): child is Component => child !== undefined)
        .map(child => enhanceComponent(child));
    } else {
      // Fallback: find children by parentId
      const parentIdChildren = parentIdChildrenMap.get(component.id);
      if (parentIdChildren && parentIdChildren.length > 0) {
        childComponents = parentIdChildren.map(child => enhanceComponent(child));
      }
    }

    if (childComponents.length > 0) {
      return {
        ...component,
        _childComponents: childComponents
      };
    }

    return component;
  };

  // Enhance all components and filter out children (keep only root-level components)
  return components
    .filter(component => !childComponentIds.has(component.id))
    .map(component => enhanceComponent(component));
};

const normalizeIncomingComponent = (
  comp: Partial<Component>,
  project: Project,
  pageId: string,
): Component | null => {
  if (!comp.type) {
    console.warn(
      `[viewer hydration] Skipping component with missing type (id: ${comp.id ?? "unknown"})`,
    );
    return null;
  }

  return {
    id: comp.id || uuidv4(),
    projectId: comp.projectId || project.id,
    pageId: comp.pageId || pageId,
    name: comp.name || `${comp.type} Component`,
    type: comp.type,
    properties: comp.properties || {},
    position: comp.position || { x: 0, y: 0, z: 0 },
    size: comp.size || { width: 200, height: 100 },
    style: comp.style || {},
    responsivePosition:
      comp.responsivePosition ||
      responsivePositionTranslator.generateDefaultResponsivePosition(comp.type),
    parentId: comp.parentId,
    children: comp.children || [],
    order: comp.order || 0,
    locked: comp.locked || false,
    hidden: comp.hidden || false,
    version: comp.version || 1,
    createdBy: comp.createdBy || project.ownerId,
    lastModifiedBy: comp.lastModifiedBy || project.ownerId,
    lastModifiedAt: comp.lastModifiedAt
      ? new Date(comp.lastModifiedAt)
      : new Date(),
    isDeleted: comp.isDeleted || false,
    deletedAt: comp.deletedAt ? new Date(comp.deletedAt) : undefined,
    createdAt: comp.createdAt ? new Date(comp.createdAt) : new Date(),
    updatedAt: comp.updatedAt ? new Date(comp.updatedAt) : new Date(),
  } satisfies Component;
};

const getNumericComponentDimension = (value: number | string): number => {
  return typeof value === "number" ? value : 0;
};

type ViewerPageSnapshotSeed = Pick<PageSnapshot, "pageId" | "pageName"> &
  Partial<Pick<PageSnapshot, "components" | "canvasSettings" | "metadata">>;

const buildViewerPagesFromSnapshot = (
  project: Project,
  snapshotPages: ViewerPageSnapshotSeed[],
): Page[] => {
  const now = new Date();

  return snapshotPages.map((pageSnapshot, index) => ({
    id: pageSnapshot.pageId,
    projectId: project.id,
    name: pageSnapshot.pageName || (index === 0 ? "Home" : `Page ${index + 1}`),
    description: undefined,
    order: index,
    isDefault: index === 0,
    isStarred: false,
    template: PageTemplate.BLANK,
    navigation: {
      showInNavigation: true,
      navigationLabel: pageSnapshot.pageName || undefined,
      allowNavigation: true,
      requiresAuth: false,
      icon: undefined,
      order: index,
    },
    permissions: {
      canView: [
        ProjectRole.OWNER,
        ProjectRole.ADMIN,
        ProjectRole.EDITOR,
        ProjectRole.VIEWER,
      ],
      canEdit: [ProjectRole.OWNER, ProjectRole.ADMIN, ProjectRole.EDITOR],
      canDelete: [ProjectRole.OWNER, ProjectRole.ADMIN],
      canShare: [ProjectRole.OWNER, ProjectRole.ADMIN],
      inheritFromProject: true,
      customPermissions: [],
    },
    canvasSettings: pageSnapshot.canvasSettings || defaultViewerCanvasSettings,
    metadata: pageSnapshot.metadata || {
      viewCount: 0,
      editCount: 0,
      componentCount: Array.isArray(pageSnapshot.components)
        ? pageSnapshot.components.length
        : 0,
      tags: [],
      customFields: {},
    },
    createdBy: project.ownerId || "unknown",
    lastModifiedBy: project.ownerId || "unknown",
    lastModifiedAt: now,
    createdAt: now,
    updatedAt: now,
  }));
};

interface ViewerState {
  // Core state
  project: Project | null;
  components: Component[];
  componentMap: Map<string, Component>;

  // Multi-page state
  pages: Page[];
  pageMap: Map<string, Page>;
  pageComponents: Map<string, Component[]>; // Components by page ID
  currentPageId: string | null;
  currentPage: Page | null;

  // View settings
  viewport: Viewport;
  zoom: number;
  gridEnabled: boolean;
  gridSize: number;
  canvasSettings: CanvasSettings;

  // Loading and error states
  isLoading: boolean;
  error: string | null;
  lastUpdateTimestamp: number;

  // Viewer-specific settings
  isFullscreen: boolean;
  showInfo: boolean;
  showComponentLabels: boolean;
  allowInteraction: boolean; // For components that support limited interaction in view mode

  // Button toggle states (for toggle buttons like notifications)
  activeButtons: Set<string>;

  // Component toggle states (for toggled components like sidebars)
  toggledComponents: Set<string>;

  // Scoped org override for cross-org data access in viewer
  viewerOrgId: string | null;
  viewerOrgSlug: string | null;

  // Performance optimization
  enableVirtualization: boolean;
  visibleBounds: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
}

type LoadedViewerProject = Project & {
  canvasSettings?: CanvasSettings;
  components?: Component[];
};

interface ViewerActions {
  // Project actions
  loadProject: (projectId: string, pageId?: string, forceReload?: boolean) => Promise<void>;
  refreshProject: () => Promise<void>;
  clearProject: () => void;

  // Multi-page actions
  switchToPage: (pageId: string) => void;
  getCurrentPageComponents: () => Component[];
  getPageComponents: (pageId: string) => Component[];
  isMultiPageProject: () => boolean;

  // View actions
  setViewport: (viewport: Viewport) => void;
  setZoom: (zoom: number) => void;
  resetView: () => void;
  toggleFullscreen: () => void;

  // Grid actions
  toggleGrid: () => void;
  setGridSize: (size: number) => void;

  // Canvas settings
  updateCanvasSettings: (settings: Partial<CanvasSettings>) => void;

  // UI actions
  toggleInfo: () => void;
  toggleComponentLabels: () => void;
  setAllowInteraction: (allow: boolean) => void;

  // Button toggle actions
  toggleButtonActive: (buttonId: string) => void;
  isButtonActive: (buttonId: string) => boolean;

  // Component toggle actions (for toggled components like sidebars)
  toggleComponent: (componentId: string) => void;
  isComponentToggled: (componentId: string) => boolean;
  setComponentToggled: (componentId: string, toggled: boolean) => void;

  // Component actions (for interactive viewer mode)
  updateComponent: (componentId: string, updates: Partial<Component>) => void;

  // Org slug setter
  setViewerOrgSlug: (slug: string | null) => void;

  // Performance optimization
  setVisibleBounds: (
    bounds: { x: number; y: number; width: number; height: number } | null,
  ) => void;
  setVirtualizationEnabled: (enabled: boolean) => void;

  // Utility actions
  getComponentById: (id: string) => Component | undefined;
  getComponentsInBounds: (bounds: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => Component[];

  // Error handling
  setError: (error: string | null) => void;
  clearError: () => void;
}

interface ViewerStore extends ViewerState, ViewerActions {}

export const useViewerStore = create<ViewerStore>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // Initial state
      project: null,
      components: [],
      componentMap: new Map(),

      // Multi-page initial state
      pages: [],
      pageMap: new Map(),
      pageComponents: new Map(),
      currentPageId: null,
      currentPage: null,

      // Default view settings
      viewport: Viewport.DESKTOP,
      zoom: 1,
      gridEnabled: false,
      gridSize: 20,
      canvasSettings: defaultViewerCanvasSettings,

      // Loading states
      isLoading: false,
      error: null,
      lastUpdateTimestamp: Date.now(),

      // Viewer-specific settings
      isFullscreen: false,
      showInfo: true,
      showComponentLabels: false,
      allowInteraction: false,

      // Button toggle states
      activeButtons: new Set(),

      // Component toggle states
      toggledComponents: new Set(),

      // Scoped org override
      viewerOrgId: null,
      viewerOrgSlug: null,

      // Performance settings
      enableVirtualization: true,
      visibleBounds: null,

      // Actions implementation
      loadProject: async (projectId: string, pageId?: string, forceReload = false) => {
        const state = get();


        // Only prevent if we're currently loading the exact same project AND same page AND not forcing reload
        const isSameProject = state.project?.id === projectId;
        const isSamePage = pageId === state.currentPageId || (!pageId && !state.currentPageId);
        const shouldSkip = state.isLoading && isSameProject && isSamePage && !forceReload;

        if (shouldSkip) {
          return;
        }

        // Always log when loading different projects
        if (!isSameProject) {
        }

        // Clear previous project data to prevent memory leaks
        const { clearProject } = get();
        clearProject();

        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          // Validate projectId
          if (!projectId) {
            throw new Error("Project ID is required");
          }

          // Fetch project data from API using apiClient for proper error handling and 429 retry logic
          const response = await apiClient.get(`/projects/${projectId}`);

          if (!response.data) {
            throw new Error("Failed to load project: No data received");
          }

          // Handle both wrapped {data: project} and unwrapped {project} response formats
          const responseData = response.data;
          // Keep this load-time migration in memory; explicit persistence can be added
          // once project save semantics for metadata-only migrations are settled.
          const project = migrateProjectThemeColors(
            responseData.data || responseData,
          ) as LoadedViewerProject;


          set((state) => {
            state.project = project;
            state.canvasSettings =
              project.canvasSettings || defaultViewerCanvasSettings;
            state.lastUpdateTimestamp = Date.now();

            // Set viewer org override for cross-org data access
            state.viewerOrgId = project.owner_type === 'Organization' && project.owner_id
              ? project.owner_id
              : null;

            // Initialize multi-page state

            // Fix corrupted page data by checking both project pages and snapshot pages
            const rawPages: unknown[] = project.pages || [];

            // Initialize page components first to get snapshot page IDs
            // Use .find() to match by version number — NOT array index
            let currentVersion: ProjectVersion | undefined = Array.isArray(project.versions)
              ? project.versions
                  .filter((v: ProjectVersion) => v.version === project.currentVersion)
                  .sort(
                    (a: ProjectVersion, b: ProjectVersion) =>
                      getVersionCreatedAtMs(b) - getVersionCreatedAtMs(a),
                  )[0]
              : project.versions?.[project.currentVersion];

            // Handle missing current version
            if (!currentVersion && project.versions) {
              // Try to find the latest version with components (prioritize versions with actual data)
              const versionKeys = Object.keys(project.versions)
                .map(Number)
                .sort((a: number, b: number) => b - a);

              // First pass: look for versions with actual components
              for (const versionKey of versionKeys) {
                const version = project.versions[versionKey];
                if (version?.snapshot) {
                  const hasComponents =
                    (version.snapshot.pages &&
                      version.snapshot.pages.some(
                        (p: PageSnapshot) => p.components?.length && p.components.length > 0,
                      )) ||
                    (version.snapshot.components &&
                      version.snapshot.components.length > 0);

                  if (hasComponents) {
                    currentVersion = version;
                    break;
                  }
                }
              }

              // Second pass: if no version with components found, use any version with snapshot
              if (!currentVersion) {
                for (const versionKey of versionKeys) {
                  const version = project.versions[versionKey];
                  if (version?.snapshot) {
                    currentVersion = version;
                    break;
                  }
                }
              }
            }

            // Extract snapshot page data first - this is our source of truth
            const snapshotPages: Array<{ pageId: string; pageName: string; index: number }> = [];

            if (currentVersion?.snapshot?.pages) {
              currentVersion.snapshot.pages.forEach((pageSnapshot: PageSnapshot, idx: number) => {
                if (pageSnapshot.pageId) {
                  // Don't bake in fallback names here — let the second loop
                  // fall through to page?.name (the real name from project.pages)
                  snapshotPages.push({
                    pageId: pageSnapshot.pageId,
                    pageName: pageSnapshot.pageName || '',
                    index: idx
                  });
                }
              });
            }

            const buildNormalizedPage = (
              rawPage: Partial<Page>,
              index: number,
            ): Page => {
              const ownerId = project.ownerId || "unknown";
              const timestamp =
                rawPage.updatedAt ||
                rawPage.createdAt ||
                rawPage.lastModifiedAt ||
                new Date();

              return {
                id: rawPage.id || uuidv4(),
                projectId: rawPage.projectId || project.id,
                name: rawPage.name || (index === 0 ? "Home" : `Page ${index + 1}`),
                description: rawPage.description,
                order: typeof rawPage.order === "number" ? rawPage.order : index,
                isDefault:
                  typeof rawPage.isDefault === "boolean"
                    ? rawPage.isDefault
                    : index === 0,
                isStarred: rawPage.isStarred || false,
                template: rawPage.template || PageTemplate.BLANK,
                navigation: {
                  showInNavigation: true,
                  allowNavigation: true,
                  requiresAuth: false,
                  order: index,
                  ...(rawPage.navigation || {}),
                },
                permissions: rawPage.permissions || {
                  canView: [
                    ProjectRole.OWNER,
                    ProjectRole.ADMIN,
                    ProjectRole.EDITOR,
                    ProjectRole.VIEWER,
                  ],
                  canEdit: [
                    ProjectRole.OWNER,
                    ProjectRole.ADMIN,
                    ProjectRole.EDITOR,
                  ],
                  canDelete: [ProjectRole.OWNER, ProjectRole.ADMIN],
                  canShare: [ProjectRole.OWNER, ProjectRole.ADMIN],
                  inheritFromProject: true,
                  customPermissions: [],
                },
                canvasSettings: rawPage.canvasSettings || defaultViewerCanvasSettings,
                metadata: {
                  viewCount: 0,
                  editCount: 0,
                  componentCount: 0,
                  tags: [],
                  customFields: {},
                  ...(rawPage.metadata || {}),
                },
                createdBy: rawPage.createdBy || ownerId,
                lastModifiedBy: rawPage.lastModifiedBy || ownerId,
                lastModifiedAt: new Date(timestamp),
                createdAt: new Date(rawPage.createdAt || timestamp),
                updatedAt: new Date(rawPage.updatedAt || timestamp),
              };
            };

            // 🚀 FIX: Preserve original page IDs to ensure homePageId lookups work correctly
            // Build a mapping from snapshot pageId to original page.id for component retrieval
            const snapshotToOriginalIdMap = new Map<string, string>();

            const validPages: Page[] = rawPages
              .map((item: unknown, index: number) => {
                if (isWrappedPageRecord(item)) {
                  return buildNormalizedPage(item.page, index);
                }

                if (isSnapshotPageRecord(item)) {
                  return buildNormalizedPage(
                    {
                      id: item.pageId,
                      projectId: project.id,
                      name: item.pageName,
                      order: index,
                      isDefault: index === 0,
                      canvasSettings: item.canvasSettings,
                      navigation: item.navigation,
                      metadata: item.metadata,
                    },
                    index,
                  );
                }

                if (
                  item !== null &&
                  typeof item === "object" &&
                  "id" in item &&
                  typeof (item as Page).id === "string"
                ) {
                  return buildNormalizedPage(item as Page, index);
                }

                return null;
              })
              .filter((page): page is Page => page !== null);

            if (validPages.length === 0 && snapshotPages.length > 0) {
              snapshotPages.forEach((snapshotPage, index) => {
                validPages.push(
                  buildNormalizedPage(
                    {
                      id: snapshotPage.pageId,
                      projectId: project.id,
                      name: snapshotPage.pageName,
                      order: index,
                      isDefault: index === 0,
                    },
                    index,
                  ),
                );
              });
            }

            snapshotPages.forEach((snapshotPage, index) => {
              const preservedId = validPages[index]?.id || snapshotPage.pageId;
              snapshotToOriginalIdMap.set(snapshotPage.pageId, preservedId);
            });

            state.pages = validPages;

            // Initialize pageMap - now all pages should have valid IDs aligned with snapshots
            state.pageMap = new Map(
              validPages.map((page: Page) => [page.id, page])
            );

            // Initialize page components
            if (project.pages?.length > 0) {
              let hasLoadedComponents = false;

              // Handle multi-page project
              if (currentVersion?.snapshot?.pages) {
                currentVersion.snapshot.pages.forEach((pageSnapshot: PageSnapshot) => {

                  // Ensure all components have responsivePosition
                  const migratedComponents = (pageSnapshot.components || []).map((c: Component) => {
                    if (!c.responsivePosition) {
                      return {
                        ...c,
                        responsivePosition: responsivePositionTranslator.generateDefaultResponsivePosition(c.type)
                      };
                    }
                    return c;
                  });

                  // 🚀 FIX: Use preserved page ID (from validPages) instead of snapshot pageId
                  // This ensures component retrieval works with original page IDs
                  const preservedPageId = snapshotToOriginalIdMap.get(pageSnapshot.pageId) || pageSnapshot.pageId;

                  state.pageComponents.set(
                    preservedPageId,
                    migrateViewerComponents(migratedComponents),
                  );

                  if (migratedComponents.length > 0) {
                    hasLoadedComponents = true;
                  }
                });
              } else if (
                currentVersion?.snapshot?.components &&
                !currentVersion.snapshot.pages
              ) {
                // Legacy migration: put all components on the default page
                const defaultPage =
                  project.pages.find((p: Page) => p.isDefault) || project.pages[0];
                if (defaultPage) {
                  state.pageComponents.set(
                    defaultPage.id,
                    migrateViewerComponents(
                      currentVersion.snapshot.components || [],
                    ),
                  );
                  hasLoadedComponents =
                    (currentVersion.snapshot.components || []).length > 0;
                }
              }

              // Fallback 1: legacy root-level project.components
              if (!hasLoadedComponents) {
                const projectComponents = (project as Project & { components?: Partial<Component>[] }).components;
                const defaultPage =
                  validPages.find((p: Page) => p.isDefault) || validPages[0];

                if (defaultPage && Array.isArray(projectComponents) && projectComponents.length > 0) {
                  const normalizedComponents = projectComponents
                    .map((comp) => normalizeIncomingComponent(comp, project, defaultPage.id))
                    .filter((comp): comp is Component => comp !== null);

                  state.pageComponents.set(
                    defaultPage.id,
                    migrateViewerComponents(normalizedComponents),
                  );
                  hasLoadedComponents = normalizedComponents.length > 0;
                }
              }

              // Fallback 2: components embedded in wrapped project.pages entries
              if (!hasLoadedComponents) {
                validPages.forEach((page: Page) => {
                  const wrappedPageRecord = rawPages.find((entry) => {
                    if (!entry || typeof entry !== "object") {
                      return false;
                    }

                    const snapshotId = Reflect.get(entry, "pageId");
                    if (snapshotId === page.id) {
                      return true;
                    }

                    const directId = Reflect.get(entry, "id");
                    if (directId === page.id) {
                      return true;
                    }

                    const nestedPage = Reflect.get(entry, "page");
                    return (
                      nestedPage !== null &&
                      typeof nestedPage === "object" &&
                      Reflect.get(nestedPage, "id") === page.id
                    );
                  });

                  const embeddedComponentsValue =
                    wrappedPageRecord && typeof wrappedPageRecord === "object"
                      ? Reflect.get(wrappedPageRecord, "components")
                      : undefined;
                  const embeddedComponents = Array.isArray(embeddedComponentsValue)
                    ? embeddedComponentsValue.filter(
                        (component): component is Partial<Component> =>
                          component !== null && typeof component === "object",
                      )
                    : [];

                  if (embeddedComponents.length > 0) {
                    const normalizedComponents = embeddedComponents
                      .map((comp) => normalizeIncomingComponent(comp, project, page.id))
                      .filter((comp): comp is Component => comp !== null);

                    state.pageComponents.set(
                      page.id,
                      migrateViewerComponents(normalizedComponents),
                    );

                    if (normalizedComponents.length > 0) {
                      hasLoadedComponents = true;
                    }
                  } else if (!state.pageComponents.has(page.id)) {
                    state.pageComponents.set(page.id, []);
                  }
                });
              }

              if (!hasLoadedComponents) {
                project.pages.forEach((page: Page) => {
                  if (page?.id && !state.pageComponents.has(page.id)) {
                    state.pageComponents.set(page.id, []);
                  }
                });
              }

              // Set current page with improved fallback logic
              // Since we've ensured all pages have IDs above, we can simplify this
              const defaultPage = validPages.find((p: Page) => p?.isDefault) || validPages[0];


              // Validate and sanitize target page ID
              let targetPageId: string | undefined;
              if (pageId && pageId !== "undefined" && pageId !== "null") {
                targetPageId = pageId;
              } else if (project.currentPageId && project.currentPageId !== "undefined") {
                targetPageId = project.currentPageId;
              } else if (defaultPage?.id) {
                targetPageId = defaultPage.id;
              }

              if (targetPageId) {
                targetPageId =
                  snapshotToOriginalIdMap.get(targetPageId) || targetPageId;
              }

              // Try to use target page if it exists and is valid
              if (targetPageId && state.pageMap.has(targetPageId)) {
                const targetPage = state.pageMap.get(targetPageId);
                if (targetPage?.id) {
                  const pageComponents = state.pageComponents.get(targetPage.id) || [];

                  state.currentPageId = targetPage.id;
                  state.currentPage = targetPage;
                  state.components = enhanceComponentsWithChildren(pageComponents);
                  state.componentMap = buildCompleteComponentMap(state.components);
                }
              } else if (validPages.length > 0) {
                // Improved fallback logic - since we've ensured all pages have IDs, use defaultPage or first page
                const fallbackPage = defaultPage;

                if (fallbackPage?.id) {
                  state.currentPageId = fallbackPage.id;
                  state.currentPage = fallbackPage;
                  state.components = enhanceComponentsWithChildren(state.pageComponents.get(fallbackPage.id) || []);
                  state.componentMap = buildCompleteComponentMap(state.components);
                } else {
                }
              } else {
              }
            } else {
              // Handle single-page project
              const legacyComponents =
                project.components ||
                project.versions?.[project.currentVersion]?.snapshot
                  ?.components ||
                [];

              // Ensure all components have responsivePosition
              const migratedLegacyComponents = legacyComponents.map((c: Component) => {
                if (!c.responsivePosition) {
                  return {
                    ...c,
                    responsivePosition: responsivePositionTranslator.generateDefaultResponsivePosition(c.type)
                  };
                }
                return c;
              });

              state.components = enhanceComponentsWithChildren(
                migrateViewerComponents(migratedLegacyComponents),
              );
              state.componentMap = buildCompleteComponentMap(state.components);
            }

            state.isLoading = false;
          });

          // Push initial navigation entry after project loads (outside set() to avoid anti-pattern)
          const { currentPage: initialPage } = get();
          if (initialPage && project) {
            useNavigationStore.getState().pushNavigation({
              projectId: project.id,
              projectName: project.name,
              pageId: initialPage.id,
              pageName: initialPage.name,
            });
          }
        } catch (error: unknown) {
          const errorMessage = getApiErrorMessage(error, "Failed to load project");
          set((state) => {
            state.error = errorMessage;
            state.isLoading = false;
          });
        }
      },

      refreshProject: async () => {
        const { project, currentPageId, loadProject } = get();
        if (project?.id) {
          await loadProject(project.id, currentPageId || undefined);
        }
      },

      clearProject: () => {
        set((state) => {
          // Clear all Maps to prevent memory leaks
          state.componentMap.clear();
          state.pageMap.clear();
          state.pageComponents.clear();
          state.toggledComponents.clear();
          state.activeButtons.clear();

          // Reset all state
          state.project = null;
          state.components = [];
          state.pages = [];
          state.currentPageId = null;
          state.currentPage = null;
          state.isLoading = false;
          state.error = null;
          state.viewerOrgId = null;
          state.viewerOrgSlug = null;
          state.visibleBounds = null;
          state.lastUpdateTimestamp = Date.now();
        });
      },

      /**
       * Set project data directly without an API call.
       * Used by the HA add-on viewer and standalone/offline mode to feed
       * pre-loaded dashboard data into the viewer rendering pipeline.
       */
      setProject: (projectData: {
        id: string;
        name: string;
        components: Component[];
        pages?: Page[];
        canvasSettings?: CanvasSettings;
        owner_id?: string;
      }) => {
        const { clearProject } = get();
        clearProject();
        const migratedProjectData = migrateProjectThemeColors(projectData);

        set((state) => {
          state.project = {
            id: migratedProjectData.id,
            name: migratedProjectData.name,
          } as Project;

          state.canvasSettings = migratedProjectData.canvasSettings || defaultViewerCanvasSettings;
          state.lastUpdateTimestamp = Date.now();

          if (migratedProjectData.owner_id) {
            state.viewerOrgId = migratedProjectData.owner_id;
          }

            if (migratedProjectData.pages && migratedProjectData.pages.length > 0) {
              // Multi-page mode
              const validPages = migratedProjectData.pages.map((page, index) => ({
                ...page,
                id: page.id || uuidv4(),
              name: page.name || (index === 0 ? 'Home' : `Page ${index + 1}`),
              isDefault: page.isDefault ?? (index === 0),
            }));

            state.pages = validPages;
            state.pageMap = new Map(validPages.map(p => [p.id, p]));

              // Components are expected to already be organized per page
              // If pages have associated components, set them; otherwise put all on first page
              const defaultPage = validPages.find(p => p.isDefault) || validPages[0];
              const normalizedComponents = migrateViewerComponents(
                migratedProjectData.components || [],
              );
              if (defaultPage) {
              state.pageComponents.set(defaultPage.id, normalizedComponents);
                state.currentPageId = defaultPage.id;
                state.currentPage = defaultPage;
              state.components = enhanceComponentsWithChildren(normalizedComponents);
                state.componentMap = buildCompleteComponentMap(state.components);
              }
            } else {
              // Single-page mode
            state.components = enhanceComponentsWithChildren(
              migrateViewerComponents(migratedProjectData.components || []),
            );
              state.componentMap = buildCompleteComponentMap(state.components);
            }

          state.isLoading = false;
        });
      },

      // Multi-page actions
      switchToPage: (pageId: string) => {
        // Get page and project info before set() for navigation
        const { pageMap, project } = get();
        const page = pageMap.get(pageId);

        if (!page) return;

        set((state) => {
          state.currentPageId = pageId;
          state.currentPage = page;
          state.components = enhanceComponentsWithChildren(state.pageComponents.get(pageId) || []);
          state.componentMap = buildCompleteComponentMap(state.components);
          state.lastUpdateTimestamp = Date.now();
        });

        // Push to navigation history (outside set() to avoid anti-pattern)
        if (project) {
          useNavigationStore.getState().pushNavigation({
            projectId: project.id,
            projectName: project.name,
            pageId: page.id,
            pageName: page.name,
          });
        }
      },

      getCurrentPageComponents: () => {
        const { currentPageId, pageComponents, components } = get();
        if (currentPageId) {
          return pageComponents.get(currentPageId) || [];
        }
        return components;
      },

      getPageComponents: (pageId: string) => {
        const { pageComponents } = get();
        return pageComponents.get(pageId) || [];
      },

      isMultiPageProject: () => true, // All projects are multi-page now

      setViewport: (viewport: Viewport) => {
        set((state) => {
          state.viewport = viewport;
          state.lastUpdateTimestamp = Date.now();
        });
      },

      setZoom: (zoom: number) => {
        const clampedZoom = Math.max(0.1, Math.min(5, zoom));
        set((state) => {
          state.zoom = clampedZoom;
          state.lastUpdateTimestamp = Date.now();
        });
      },

      resetView: () => {
        set((state) => {
          state.zoom = 1;
          state.viewport = Viewport.DESKTOP;
          state.lastUpdateTimestamp = Date.now();
        });
      },

      toggleFullscreen: () => {
        set((state) => {
          state.isFullscreen = !state.isFullscreen;
        });
      },

      toggleGrid: () => {
        set((state) => {
          state.gridEnabled = !state.gridEnabled;
          state.lastUpdateTimestamp = Date.now();
        });
      },

      setGridSize: (size: number) => {
        set((state) => {
          state.gridSize = Math.max(5, Math.min(100, size));
          state.lastUpdateTimestamp = Date.now();
        });
      },

      updateCanvasSettings: (settings: Partial<CanvasSettings>) => {
        set((state) => {
          state.canvasSettings = {
            ...state.canvasSettings,
            ...settings,
          };
          state.lastUpdateTimestamp = Date.now();
        });
      },

      toggleInfo: () => {
        set((state) => {
          state.showInfo = !state.showInfo;
        });
      },

      toggleComponentLabels: () => {
        set((state) => {
          state.showComponentLabels = !state.showComponentLabels;
        });
      },

      setAllowInteraction: (allow: boolean) => {
        set((state) => {
          state.allowInteraction = allow;
        });
      },

      setViewerOrgSlug: (slug) => {
        set((state) => {
          state.viewerOrgSlug = slug;
        });
      },

      toggleButtonActive: (buttonId: string) => {
        set((state) => {
          if (state.activeButtons.has(buttonId)) {
            state.activeButtons.delete(buttonId);
          } else {
            state.activeButtons.add(buttonId);
          }
        });
      },

      isButtonActive: (buttonId: string) => {
        return get().activeButtons.has(buttonId);
      },

      toggleComponent: (componentId: string) => {
        set((state) => {
          if (state.toggledComponents.has(componentId)) {
            state.toggledComponents.delete(componentId);
          } else {
            state.toggledComponents.add(componentId);
          }
        });
      },

      isComponentToggled: (componentId: string) => {
        return get().toggledComponents.has(componentId);
      },

      setComponentToggled: (componentId: string, toggled: boolean) => {
        set((state) => {
          if (toggled) {
            state.toggledComponents.add(componentId);
          } else {
            state.toggledComponents.delete(componentId);
          }
        });
      },

      updateComponent: (componentId: string, updates: Partial<Component>) => {
        set((state) => {
          // Helper function to recursively update a component in the tree
          const updateComponentInTree = (components: Component[]): boolean => {
            for (let i = 0; i < components.length; i++) {
              const currentComponent = components[i];
              if (!currentComponent) {
                continue;
              }

              if (currentComponent.id === componentId) {
                // Found the component, update it
                components[i] = {
                  ...currentComponent,
                  ...updates,
                };
                // Update componentMap
                state.componentMap.set(componentId, components[i]);
                return true;
              }

              // Check nested children
              if (currentComponent._childComponents && Array.isArray(currentComponent._childComponents)) {
                const childComponents = currentComponent._childComponents;
                if (updateComponentInTree(childComponents)) {
                  return true;
                }
              }
            }
            return false;
          };

          // Update in components array (includes nested children)
          updateComponentInTree(state.components);

          // Update in pageComponents if multi-page
          if (state.currentPageId && state.pageComponents.has(state.currentPageId)) {
            const pageComponents = state.pageComponents.get(state.currentPageId);
            if (pageComponents) {
              // Update in raw pageComponents (flat array before enhancement)
              const pageComponentIndex = pageComponents.findIndex(c => c.id === componentId);
              if (pageComponentIndex !== -1) {
                pageComponents[pageComponentIndex] = {
                  ...pageComponents[pageComponentIndex],
                  ...updates,
                };
              }
            }
          }

          state.lastUpdateTimestamp = Date.now();
        });
      },

      setVisibleBounds: (
        bounds: { x: number; y: number; width: number; height: number } | null,
      ) => {
        set((state) => {
          state.visibleBounds = bounds;
        });
      },

      setVirtualizationEnabled: (enabled: boolean) => {
        set((state) => {
          state.enableVirtualization = enabled;
        });
      },

      getComponentById: (id: string) => {
        return get().componentMap.get(id);
      },

      getComponentsInBounds: (bounds: {
        x: number;
        y: number;
        width: number;
        height: number;
      }) => {
        const { components } = get();

        return components.filter((component) => {
          // Skip components without position (e.g., children in flex containers)
          if (!component.position) {
            return false;
          }

          const width = getNumericComponentDimension(component.size.width);
          const height = getNumericComponentDimension(component.size.height);

          if (width === 0 || height === 0) {
            return false;
          }

          const cLeft = component.position.x;
          const cTop = component.position.y;
          const cRight = cLeft + width;
          const cBottom = cTop + height;

          const bRight = bounds.x + bounds.width;
          const bBottom = bounds.y + bounds.height;

          // Check if component intersects with bounds
          return !(
            cRight < bounds.x ||
            cLeft > bRight ||
            cBottom < bounds.y ||
            cTop > bBottom
          );
        });
      },

      setError: (error: string | null) => {
        set((state) => {
          state.error = error;
        });
      },

      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },
    })),
  ),
);

// Selectors for optimized component rendering
export const useViewerComponents = () =>
  useViewerStore((state) => state.components);
export const useViewerProject = () => useViewerStore((state) => state.project);

// Selector for componentMap - used for child lookup in FormContainer/FlexContainer
// Note: Returns the Map directly to avoid creating new arrays on each render
export const useViewerComponentMap = () =>
  useViewerStore((state) => state.componentMap);

// Multi-page selectors
export const useViewerPages = () => useViewerStore((state) => state.pages);
export const useViewerCurrentPage = () =>
  useViewerStore((state) => state.currentPage);
export const useViewerCurrentPageId = () =>
  useViewerStore((state) => state.currentPageId);
export const useViewerMultiPageMode = () => true; // All projects are multi-page now

// Individual selectors to avoid object creation
export const useViewerViewport = () =>
  useViewerStore((state) => state.viewport);
export const useViewerZoom = () => useViewerStore((state) => state.zoom);
export const useViewerGridEnabled = () =>
  useViewerStore((state) => state.gridEnabled);
export const useViewerGridSize = () =>
  useViewerStore((state) => state.gridSize);
export const useViewerCanvasSettings = () =>
  useViewerStore((state) => state.canvasSettings);

export const useViewerIsFullscreen = () =>
  useViewerStore((state) => state.isFullscreen);
export const useViewerShowInfo = () =>
  useViewerStore((state) => state.showInfo);
export const useViewerShowComponentLabels = () =>
  useViewerStore((state) => state.showComponentLabels);
export const useViewerAllowInteraction = () =>
  useViewerStore((state) => state.allowInteraction);

export const useViewerIsLoading = () =>
  useViewerStore((state) => state.isLoading);
export const useViewerError = () => useViewerStore((state) => state.error);

// Composite selectors for backward compatibility (with stable references)
export const useViewerSettings = () => {
  const viewport = useViewerViewport();
  const zoom = useViewerZoom();
  const gridEnabled = useViewerGridEnabled();
  const gridSize = useViewerGridSize();
  const canvasSettings = useViewerCanvasSettings();

  return useMemo(
    () => ({
      viewport,
      zoom,
      gridEnabled,
      gridSize,
      canvasSettings,
    }),
    [viewport, zoom, gridEnabled, gridSize, canvasSettings],
  );
};

export const useViewerUI = () => {
  const isFullscreen = useViewerIsFullscreen();
  const showInfo = useViewerShowInfo();
  const showComponentLabels = useViewerShowComponentLabels();
  const allowInteraction = useViewerAllowInteraction();

  return useMemo(
    () => ({
      isFullscreen,
      showInfo,
      showComponentLabels,
      allowInteraction,
    }),
    [isFullscreen, showInfo, showComponentLabels, allowInteraction],
  );
};

export const useViewerOrgId = () =>
  useViewerStore((state) => state.viewerOrgId);

export const useViewerLoadingState = () => {
  const isLoading = useViewerIsLoading();
  const error = useViewerError();

  return useMemo(
    () => ({
      isLoading,
      error,
    }),
    [isLoading, error],
  );
};

// Performance optimization selector for virtualized rendering
export const useVisibleComponents = () => {
  const components = useViewerComponents();
  const enableVirtualization = useViewerStore(
    (state) => state.enableVirtualization,
  );
  const visibleBounds = useViewerStore((state) => state.visibleBounds);
  const getComponentsInBounds = useViewerStore(
    (state) => state.getComponentsInBounds,
  );

  return useMemo(() => {
    // Base filtering for hidden/deleted components
    const baseFiltered = components.filter((c: Component) => !c.hidden && !c.isDeleted);

    if (!enableVirtualization || !visibleBounds) {
      return baseFiltered;
    }

    const visibleComponents = getComponentsInBounds(visibleBounds);
    const finalVisible = visibleComponents.filter(
      (c: Component) => !c.hidden && !c.isDeleted,
    );

    return finalVisible;
  }, [
    components,
    enableVirtualization,
    visibleBounds,
    getComponentsInBounds,
  ]);
};

// Persist viewer preferences to localStorage
useViewerStore.subscribe(
  (state) => ({
    showInfo: state.showInfo,
    showComponentLabels: state.showComponentLabels,
    allowInteraction: state.allowInteraction,
    enableVirtualization: state.enableVirtualization,
  }),
  (viewerPreferences) => {
    try {
      localStorage.setItem(
        "terramatrix-viewer-preferences",
        JSON.stringify(viewerPreferences),
      );
    } catch (error) {
      // Failed to save viewer preferences
    }
  },
);

// Load viewer preferences from localStorage on initialization
try {
  const savedPreferences = localStorage.getItem(
    "terramatrix-viewer-preferences",
  );
  if (savedPreferences) {
    const raw: unknown = JSON.parse(savedPreferences);
    const result = ViewerPreferencesSchema.safeParse(raw);
    if (result.success) {
      const preferences = result.data;
      useViewerStore.setState((state) => ({
        ...state,
        showInfo: preferences.showInfo ?? state.showInfo,
        showComponentLabels: preferences.showComponentLabels ?? state.showComponentLabels,
        allowInteraction: preferences.allowInteraction ?? state.allowInteraction,
        enableVirtualization: preferences.enableVirtualization ?? state.enableVirtualization,
      }));
    }
  }
} catch (error) {
  // Failed to load viewer preferences
}
